import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, InputGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ApiContext } from 'components/ApiProvider';
import ReactTooltip from 'react-tooltip';
import { toast } from 'react-toastify';

const resolver = yupResolver(
  yup.object().shape({
    newPassword: yup
      .string()
      .trim()
      .required('New password is required')
      .test(
        'isValidLength',
        'Password must have at least 8 characters',
        (value) => value?.length >= 8
      )
      .test('hasLoverCaseLetter', 'Password must have at least one lower case letter', (value) =>
        /[a-z]/.test(value)
      )
      .test('hasUpperCaseLetter', 'Password must have at least one upper case letter', (value) =>
        /[A-Z]/.test(value)
      )
      .test('hasNumber', 'Password must have at least one number', (value) => /[0-9]/.test(value)),
    newPasswordConfirmation: yup
      .string()
      .trim()
      .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
      .required('Confirm new password is required'),
  })
);

const ChangePasswordForm = ({ resetPasswordToken }) => {
  const history = useHistory();
  const { changePassword } = useContext(ApiContext);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isNewPasswordConfirmationVisible, setIsNewPasswordConfirmationVisible] = useState(false);
  const [isInstructionTooltip, setIsInstructionTooltip] = useState(true);

  const toggleNewPasswordVisibility = () => {
    setIsNewPasswordVisible(!isNewPasswordVisible);
  };

  const toggleNewPasswordConfirmationVisibility = () => {
    setIsNewPasswordConfirmationVisible(!isNewPasswordConfirmationVisible);
  };

  const changeUserPassword = async (values) => {
    try {
      await changePassword(values.newPassword, resetPasswordToken);
      history.push('/login');
      toast.success('Your password has been successfully changed!', {
        toastId: 'successToast',
      });
    } catch (e) {
      toast.error(e.response.data, {
        toastId: 'errorToast',
      });
    }
  };

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({ criteriaMode: 'all', resolver });

  useEffect(() => {
    if (errors.newPassword) {
      setIsInstructionTooltip(false);
    }

    console.log(errors.newPassword?.types?.isValidLength ? 'invalid' : 'valid');
  }, [errors.newPassword]);

  return (
    <Form
      className="form my-4 password-form"
      onSubmit={handleSubmit((values) => changeUserPassword(values))}
    >
      <div className="reset-password-form">
        <Form.Label align="right" className="subtitle">
          New Password:
        </Form.Label>
        <InputGroup className="mb-3">
          <Form.Control
            className="password-input"
            {...register('newPassword')}
            isInvalid={errors.newPassword}
            data-tip
            type={isNewPasswordVisible ? 'text' : 'password'}
          />
          <ReactTooltip
            place="right"
            className="tooltip tooltip-password"
            effect="solid"
            events={['hover', 'focus']}
          >
            {isInstructionTooltip && (
              <>
                <div>
                  <strong>Password Tips:</strong>
                  <ul>
                    <li>Use 8-14 characters.</li>
                    <li>Don&apos;t use your full name, account name or email.</li>
                    <li>Use mix of uppercase and lowercase letters, numbers and symbols</li>
                  </ul>
                </div>
              </>
            )}
            {!isInstructionTooltip && (
              <>
                <div>
                  <strong>Password must:</strong>
                  <ul>
                    <li className={errors.newPassword?.types?.isValidLength ? 'invalid' : 'valid'}>
                      Have at least 8 characters
                    </li>
                    <li
                      className={
                        errors.newPassword?.types?.hasLoverCaseLetter ? 'invalid' : 'valid'
                      }
                    >
                      Have at least one lower case letter
                    </li>
                    <li
                      className={
                        errors.newPassword?.types?.hasUpperCaseLetter ? 'invalid' : 'valid'
                      }
                    >
                      Have at least one upper case letter
                    </li>
                    <li className={errors.newPassword?.types?.hasNumber ? 'invalid' : 'valid'}>
                      Have at least one number
                    </li>
                  </ul>
                </div>
              </>
            )}
          </ReactTooltip>
          <button
            type="button"
            className="button visibility"
            onClick={() => toggleNewPasswordVisibility()}
            tabIndex={-1}
          >
            <i className={`bi ${isNewPasswordVisible ? 'bi-eye' : 'bi-eye-slash'}`} />
          </button>
          <Form.Control.Feedback type="invalid">
            {errors.newPassword?.message}
          </Form.Control.Feedback>
        </InputGroup>
        <Form.Label align="right" className="subtitle">
          Confirm New Password:
        </Form.Label>
        <InputGroup className="mb-3">
          <Form.Control
            className="password-input"
            {...register('newPasswordConfirmation')}
            isInvalid={errors.newPasswordConfirmation}
            type={isNewPasswordConfirmationVisible ? 'text' : 'password'}
          />
          <button
            type="button"
            className="button visibility"
            onClick={() => toggleNewPasswordConfirmationVisibility()}
            tabIndex={-1}
          >
            <i className={`bi ${isNewPasswordConfirmationVisible ? 'bi-eye' : 'bi-eye-slash'}`} />
          </button>
          <Form.Control.Feedback type="invalid">
            {errors.newPasswordConfirmation?.message}
          </Form.Control.Feedback>
        </InputGroup>
        <button className="button button--dark-blue mt-3 px-4 py-2 w-100" type="submit">
          Change Password
        </button>
      </div>
    </Form>
  );
};

export default ChangePasswordForm;
